import * as Yup from 'yup';

import { VALIDATIONS_ERROR } from '~constants/index';
import {
  PreferencesTrackingComplaintChannelTypeId,
  RevisitExhaustedReasonType,
} from '~globals/types/enums';

import { PREFERENCES_FIELDS_NAME as FIELDS } from './fields';
import {
  MAX_REVISIT,
  validateExhaustedReasonType,
} from './revisitExhaustedReason';
import { validateComplaintType } from './complaint';

export const validateVolumetricWeight = Yup.string().when(
  FIELDS.USE_VOLUMETRIC_WEIGHT,
  {
    is: true,
    then: (schema) => schema.required(VALIDATIONS_ERROR.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  },
);

export const validateMaxRevisitCount = Yup.string().when(
  FIELDS.SHOW_AS_NEW_ON_REJECTED,
  {
    is: true,
    then: (schema) =>
      schema
        .required(VALIDATIONS_ERROR.REQUIRED)
        .test(
          'max-revisit-range',
          'Debe ser un número entero entre 1 y 10',
          (value) => {
            const parsedValue = Number(value);

            return (
              Number.isInteger(parsedValue) &&
              parsedValue >= 1 &&
              parsedValue <= MAX_REVISIT
            );
          },
        ),
    otherwise: (schema) => schema.notRequired(),
  },
);

export const validateRevisitExhaustedReasonType = Yup.string().when(
  FIELDS.SHOW_AS_NEW_ON_REJECTED,
  {
    is: true,
    then: (schema) => schema.required(VALIDATIONS_ERROR.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  },
);

export const validateRevisitExhaustedRejectedReason = Yup.string().when(
  [FIELDS.SHOW_AS_NEW_ON_REJECTED, FIELDS.REVISIT_EXHAUSTED_REASON_TYPE],
  {
    is: (showAsNewOnRejected: boolean, revisitExhaustedReasonType: string) => {
      if (!showAsNewOnRejected) return false;

      return validateExhaustedReasonType(
        Number(revisitExhaustedReasonType),
        RevisitExhaustedReasonType.RejectedReason,
      );
    },
    then: (schema) => schema.required(VALIDATIONS_ERROR.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  },
);

export const validateRevisitExhaustedCustomReason = Yup.string().when(
  [FIELDS.SHOW_AS_NEW_ON_REJECTED, FIELDS.REVISIT_EXHAUSTED_REASON_TYPE],
  {
    is: (showAsNewOnRejected: boolean, revisitExhaustedReasonType: string) => {
      if (!showAsNewOnRejected) return false;

      return validateExhaustedReasonType(
        Number(revisitExhaustedReasonType),
        RevisitExhaustedReasonType.Custom,
      );
    },
    then: (schema) =>
      schema
        .required(VALIDATIONS_ERROR.REQUIRED)
        .max(500, 'Máximo 500 caracteres'),
    otherwise: (schema) => schema.notRequired(),
  },
);

export const validateComplaintEmail = Yup.string().when(
  FIELDS.COMPLAINT_CHANNEL_TYPE,
  {
    is: (currentVal: string) =>
      validateComplaintType(
        Number(currentVal),
        PreferencesTrackingComplaintChannelTypeId.Email,
      ),
    then: (schema) =>
      schema
        .email(VALIDATIONS_ERROR.EMAIL)
        .required(VALIDATIONS_ERROR.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  },
);

export const validateComplaintPhone = Yup.string().when(
  FIELDS.COMPLAINT_CHANNEL_TYPE,
  {
    is: (currentVal: string) =>
      validateComplaintType(
        Number(currentVal),
        PreferencesTrackingComplaintChannelTypeId.Whatsapp,
      ),
    then: (schema) => schema.required(VALIDATIONS_ERROR.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  },
);

export const preferencesValidationSchema = Yup.object().shape({
  // Operación
  [FIELDS.DEPOSIT_SERVICE_TIME]: Yup.string().notRequired(),
  [FIELDS.VISIT_SERVICE_TIME]: Yup.string().notRequired(),
  [FIELDS.ALERT_ASSIGN_DRIVER_TIME]: Yup.number().notRequired(),
  [FIELDS.ALERT_BEGIN_ROUTE_TIME]: Yup.number().notRequired(),
  [FIELDS.CARRIER_CAN_MODIFY_ITEMS]: Yup.boolean(),

  // Pedidos y carga
  [FIELDS.UNIT_1_CAPACITY_TYPE_ID]: Yup.string().required(),
  [FIELDS.UNIT_2_CAPACITY_TYPE_ID]: Yup.string().required(),
  [FIELDS.USE_VOLUMETRIC_WEIGHT]: Yup.boolean(),
  [FIELDS.VOLUMETRIC_WEIGHT_CAPACITY_FACTOR]: validateVolumetricWeight,
  [FIELDS.VOLUMETRIC_WEIGHT_THRESHOLD]: validateVolumetricWeight,
  [FIELDS.AUTO_SUM_ITEM_TASK_CASH]: Yup.boolean(),
  [FIELDS.AUTO_SUM_ITEM_TASK_UNIT_1]: Yup.boolean(),
  [FIELDS.AUTO_SUM_ITEM_TASK_UNIT_2]: Yup.boolean(),
  [FIELDS.SERVICE_TYPE_LIST]: Yup.array().of(Yup.string()),
  [FIELDS.SKILLS_LIST]: Yup.array().of(Yup.string()),

  // Etiquetas y documentos
  [FIELDS.ZEBRA_MODE_IN_TICKET_PDF]: Yup.boolean(),
  [FIELDS.INCLUDE_DIE_IN_TICKET_PDF]: Yup.boolean(),
  [FIELDS.ONE_TICKET_BY_ARTICLE]: Yup.boolean(),
  [FIELDS.ROADMAP_COLUMNS_LIST]: Yup.array()
    .of(Yup.string())
    .min(3, 'El mínimo de columnas debe ser 3'),
  [FIELDS.REPORT_FORMAT_TYPE_ID]: Yup.number(),

  // App de choferes
  [FIELDS.ALLOW_NEW_ROUTES_MOBILE]: Yup.boolean(),
  [FIELDS.DRIVER_PERMISSION_ALLOW_NEW_ITEMS]: Yup.boolean(),
  [FIELDS.DRIVER_PERMISSION_ALLOW_DELIVERY_COLLECT]: Yup.boolean(),
  [FIELDS.DRIVER_PERMISSION_ALLOW_REOPTIMIZE_ROUTES]: Yup.boolean(),
  [FIELDS.DRIVER_PERMISSION_IMAGE_LIBRARY]: Yup.boolean(),
  [FIELDS.DRIVER_PERMISSION_ROUTE_HISTORY]: Yup.boolean(),
  [FIELDS.DRIVER_PERMISSION_BLOCK_ITEM_TASKS]: Yup.boolean(),
  [FIELDS.ASK_DRIVER_TO_CONFIRM_ARRIVAL]: Yup.boolean(),
  [FIELDS.MANDATORY_LOAD_CONTROL]: Yup.boolean(),
  [FIELDS.PREVENT_MISSING_ITEMS_ON_ROUTE]: Yup.boolean(),
  [FIELDS.ALLOW_LOAD_UNSCHEDULED_ITEMS]: Yup.boolean(),
  [FIELDS.SHOW_ONLY_REQUIRED_FIELDS]: Yup.boolean(),
  [FIELDS.FEEDBACK_REQUEST_SIGN]: Yup.boolean(),
  [FIELDS.FEEDBACK_REQUEST_RECEPTOR_NAME]: Yup.boolean(),
  [FIELDS.FEEDBACK_REQUEST_RECEPTOR_ID]: Yup.boolean(),
  [FIELDS.FEEDBACK_REQUEST_DOCUMENTATION]: Yup.boolean(),
  [FIELDS.FEEDBACK_REQUEST_PICTURE]: Yup.boolean(),
  [FIELDS.FEEDBACK_REQUEST_NOTES]: Yup.boolean(),
  [FIELDS.FEEDBACK_REQUEST_AUDIT]: Yup.boolean(),
  [FIELDS.FEEDBACK_REQUEST_PICTURE_ON_REJECTED]: Yup.boolean(),
  [FIELDS.FEEDBACK_REQUEST_NOTES_ON_REJECTED]: Yup.boolean(),
  [FIELDS.FEEDBACK_REQUEST_REJECTED_REASON]: Yup.array().of(Yup.string()),
  [FIELDS.SHOW_AS_NEW_ON_REJECTED]: Yup.boolean(),
  [FIELDS.MAX_REVISIT_COUNT]: validateMaxRevisitCount,
  [FIELDS.REVISIT_EXHAUSTED_REASON_TYPE]: validateRevisitExhaustedReasonType,
  [FIELDS.REVISIT_EXHAUSTED_REJECTED_REASON]:
    validateRevisitExhaustedRejectedReason,
  [FIELDS.REVISIT_EXHAUSTED_CUSTOM_REASON]:
    validateRevisitExhaustedCustomReason,
  [FIELDS.UNASSIGN_DRIVER_ON_FORCED_CLOSURE]: Yup.boolean(),
  [FIELDS.AUTO_COMPLETE_WITH_ML]: Yup.boolean(),
  [FIELDS.AUTO_FULLFILL_WITH_ML]: Yup.boolean(),

  // Experiencia del cliente
  [FIELDS.SHOW_TRACKING_MAP]: Yup.boolean(),
  [FIELDS.COMPLAINT_CHANNEL_TYPE]: Yup.string().required(
    VALIDATIONS_ERROR.REQUIRED,
  ),
  [FIELDS.COMPLAINT_EMAIL]: validateComplaintEmail,
  [FIELDS.COMPLAINT_PHONE]: validateComplaintPhone,
  [FIELDS.ALLOW_USER_DOWNLOAD_EVIDENCE]: Yup.boolean(),
  [FIELDS.INCLUDE_MAP_IN_EVIDENCE]: Yup.boolean(),
  [FIELDS.SHOW_ITEMTASK_ON_EVIDENCE_PDF]: Yup.boolean(),
  [FIELDS.SEND_ETA_TO_CUSTOMERS]: Yup.boolean(),
  [FIELDS.TRACKING_MAIL_IN_DELIVERY_ENABLED]: Yup.boolean(),
  [FIELDS.TRACKING_MAIL_IN_DELIVERY]: Yup.string().notRequired(),
  [FIELDS.TRACKING_MAIL_IN_PROGRESS_ENABLED]: Yup.boolean(),
  [FIELDS.TRACKING_MAIL_IN_PROGRESS]: Yup.string().notRequired(),
  [FIELDS.TRACKING_MAIL_IN_DELIVERY_SUCCESS_ENABLED]: Yup.boolean(),
  [FIELDS.TRACKING_MAIL_IN_DELIVERY_SUCCESS]: Yup.string().notRequired(),
  [FIELDS.TRACKING_MAIL_IN_DELIVERY_FAIL_ENABLED]: Yup.boolean(),
  [FIELDS.TRACKING_MAIL_IN_DELIVERY_FAIL]: Yup.string().notRequired(),
  [FIELDS.TRACKING_WA_IN_DELIVERY_ENABLED]: Yup.boolean(),
  [FIELDS.TRACKING_WA_IN_PROGRESS_ENABLED]: Yup.boolean(),
  [FIELDS.TRACKING_WA_ON_THE_WAY_ENABLED]: Yup.boolean(),
  [FIELDS.TRACKING_WA_IN_DELIVERY_SUCCESS_ENABLED]: Yup.boolean(),
  [FIELDS.TRACKING_WA_IN_DELIVERY_FAIL_ENABLED]: Yup.boolean(),

  // Comercial
  [FIELDS.BILLING_LIST_ID]: Yup.string().notRequired(),
  [FIELDS.CUSTOMER_TYPE_LIST]: Yup.array().of(Yup.string()),
});
